import { action, observable } from 'mobx';
import * as PRJAPI from 'cd-eft/services/eftPrjAdminApi';
import stores from 'cd-eft/models/Stores';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import * as SETAPI from 'cd-eft/services/eftSetApi';
import { ImageDownload } from 'cd-eft/utils/ImageDownload';
import { WaterwayDesignToolsController } from 'cd-eft/services/eftSetApi';
import { TerraceDesignToolsController } from 'cd-eft/services/eftSetApi';
import { DesignElementProjectResponse, UserDefinedImages } from 'cd-eft/_codegen_/openapi-generator/eft-set';
// import { isValidBase64ImageRegex } from 'cd-eft/utils/stringUtilities';
// import { ProjectsStore } from 'cd-eft/models/Stores/ProjectsStore';

const libraryStore = stores.AppStore.projectsLibraryStore;

export interface IWaterwaySummaryStation {
  select: boolean;
  name: string;
  surface: string;
  alignment: string;
  hydrology: number;
  length: number;
  reachCount: number;
  maxTopWidth: number;
  lastUpdatedDate: string;
  alignedDesignElementId: number;
  waterwayType: string;
  waterwayTypeId?: number;
  channelType: string;
  // store plan map image, location map image and other map image
  // Images: Plan View: (imageTypeId:0/1),  Location View(imageTypeId:2),  Other View(imageTypeId:3)
  imagesDictionary: UserDefinedImages[]; // { [key: number]: string }
  threeCrosssectionImages: {};
}
export interface ITerraceSummaryStation {
  alignedElementDesignParamsId: number;
  name: string;
  terraceType: string;
  defaultTemplate: string | null;
  length: number;
  maxHeight: number;
  warnings: any | null;
  lastChangeDate: number;
  createdDate?: number;
  actions: any | null;
  errorResponse: any | null;
  imagesDictionary: UserDefinedImages[];
  threeCrosssectionImages: {};
}
export class ProjectVM {
  @observable project: any = {};
  @observable designElementTypes: DesignElementProjectResponse[] = [];
  @observable surveys: any = [];
  @observable image: string = '';
  @observable currentStructureList: any = [];
  @observable projectClassId: any = 0;
  @observable sessionId: string = '';
  @observable summaryUpdate: boolean = false;
  @observable wdtCrossSectionImageURL: any = [];
  @action.bound summaryUpdateCallback() {
    this.summaryUpdate = !this.summaryUpdate;
  }
  @observable waterwayFlagDataElements: any[] = [];
  @observable isImageLoading: boolean = false;
  @observable waterwaySummaryList: IWaterwaySummaryStation[] = [];
  @observable terraceSummaryList: ITerraceSummaryStation[] = [];
  @observable projectId: string = '';
  @observable type: any = null;
  @observable selectedImages: Array<{ stationId: string; imageURL: string; designElementId?: string }> = [];
  @observable activeTab: string = '2';

  @action.bound changeTab = (tabNumber) => {
    if (this.activeTab !== tabNumber) {
      this.activeTab = tabNumber;
    }
  };

  @action.bound async getProjectData(projectId: string) {
    libraryStore.incrementLoading();

    const project = await getProjectById(projectId);
    if (project) {
      this.project = project;
      this.projectClassId = project.projectClassId;
      if (project.sessionList && project.sessionList.length > 0) {
        this.sessionId = project.sessionList[0].sessionUuid!;
        const designElementTypes = await getAllDesignElementsBySession(this.sessionId)!;
        this.isImageLoading = true;
        if (designElementTypes && designElementTypes.length > 0) {
          const result: any[] = []
          designElementTypes.forEach(el => {
            if ([7, 19].includes(el.designElementTypeId!)) {
              result.unshift(el);
            } else {
              result.push(el)
            }
          })
          this.designElementTypes = result;
        }
        const surface = await getSurfaceDefBySession(this.sessionId);
        if (surface) this.surveys = surface.surveyMetadata;
      }
    }
    await getComments(projectId, this.projectClassId);

    const imageName = await getImage(projectId);
    this.image = await ImageDownload(imageName);
    this.isImageLoading = false;

    // this.currentStructureList provides the available Structure options for adding design comments
    if (this.designElementTypes.length) {
      this.designElementTypes.forEach((e) => {
        if (
          e.designElementTypeId === 11 ||
          e.designElementTypeId === 12 ||
          e.designElementTypeId === 13 ||
          e.designElementTypeId === 14 ||
          e.designElementTypeId === 15
        ) {
          e.designElements!.forEach((i) => {
            this.currentStructureList.push({
              id: i.designElementId,
              name: e.designElementType + ': ' + i.designElementName,
            });
          });
        }

        this.currentStructureList.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
      });
    }
    libraryStore.decrementLoading();
  }

  @action.bound async getTerraceSummary() {
    if (this.terraceSummaryList && this.terraceSummaryList.length > 0) {
      return;
    }
    libraryStore.incrementLoading();
    if (this.sessionId) {
      try {
        const response = await TerraceDesignToolsController.getTerraceDesignSummariesBySessionUuidUsingGET(this.sessionId);
        if (response && response.data) {
          const data = response.data;
          this.terraceSummaryList = data.map((e) => {
            const newSum: ITerraceSummaryStation = {
              alignedElementDesignParamsId: e.alignedElementDesignParamsId ?? -1,
              name: e.name || '',
              terraceType: e.terraceType || '',
              defaultTemplate: e.defaultTemplate || null,
              length: e.length || 0,
              maxHeight: e.maxHeight || 0,
              warnings: e.warnings || null,
              lastChangeDate: e.lastChangeDate || 0,
              createdDate: e.createdDate || 0,
              actions: e.actions || null,
              errorResponse: e.errorResponse || null,
              imagesDictionary: [],
              threeCrosssectionImages: {},
            };
            return newSum;
          });

          // Get Plan View/Location View images
          const planLocationView = await this.fetchPlanLocationViewImages(this.sessionId);

          // Fetch images for each terrace summary
          for (const summary of this.terraceSummaryList) {
            if (summary.alignedElementDesignParamsId !== -1) {
              if (planLocationView && planLocationView.length > 0) {
                summary.imagesDictionary = planLocationView;
              };
              const alignedElementDesignParamsId = summary.alignedElementDesignParamsId;
              const allImages = await this.fetchAllImages(alignedElementDesignParamsId, 4, true);
              if (allImages) {
                summary.imagesDictionary.push(...allImages);
              };
            }
          }
        }
      } catch (err) {
        console.error(err);
      }
    }
    libraryStore.decrementLoading();
  }

  @action.bound async getWaterwaySummary() {
    if (this.waterwaySummaryList.length > 0) {
      return;
    }
    libraryStore.incrementLoading();
    if (this.sessionId) {
      try {
        const response = await WaterwayDesignToolsController.getWaterwayDesignSummaryBySessionUuidUsingGET(this.sessionId);
        if (response && response.data) {
          const data = response.data;
          this.waterwaySummaryList = data.map((e) => {
            const newSum: IWaterwaySummaryStation = {
              select: false,
              name: e.name ? e.name : '',
              surface: e.surface ? e.surface : '',
              alignment: e.alignmentName ? e.alignmentName : '',
              hydrology: e.hydrology ? e.hydrology : 0,
              length: e.length ? e.length : 0,
              reachCount: e.reachCount ? e.reachCount : 0,
              maxTopWidth: e.maxTopWidth ? e.maxTopWidth : 0,
              lastUpdatedDate: e.lastUpdate ? e.lastUpdate.toString() : '',
              alignedDesignElementId:
                e.alignedElementDesignParamsId !== undefined ? e.alignedElementDesignParamsId : -1, // -1 means undefined  self-defined from FE due to typescript type integration
              channelType: e.channelType ? e.channelType : '',
              waterwayType: e.waterwayType ? e.waterwayType : '',
              waterwayTypeId: e.waterwayType === "Grassed" ? 7 : 19,
              imagesDictionary: [], // plan view, location view, other view, single cross section image
              threeCrosssectionImages: {},
            };
            return newSum;
          });
          for (const summary of this.waterwaySummaryList) {
            if (summary.alignedDesignElementId !== -1) {
              const alignedDesignElementId = summary.alignedDesignElementId;
              const allImages = await this.fetchAllImages(alignedDesignElementId, summary.waterwayTypeId ?? 0);
              if (allImages) summary.imagesDictionary = allImages;
            }
          }
        }
      } catch (err) {
        console.error(err);
      }
    }
    libraryStore.decrementLoading();
  }

  // @action.bound async fetchAndProcessThreeCrossSectionImage(alignedDesignElementId) {
  //   let result = {};
  //   const response = await SETAPI.StructureProfileController.getCrossSectionSnapshotsByAlignedElementDesignParameterIdUsingGET(alignedDesignElementId);
  //   console.log('response:', response);
  //   if (response.data) {
  //     for (const responseData of response.data) {
  //       if(responseData.stationId && responseData.imageURL){
  //         const fullPath = responseData.imageURL;
  //         const imageName = fullPath?.split('/').pop();
  //         console.log('imageName:', imageName);
  //         const base64ImageString = await ImageDownload(imageName);
  //         console.log('base64ImageString:', base64ImageString);

  //         if(isValidBase64ImageRegex(base64ImageString)){
  //           result[responseData.stationId] = base64ImageString;
  //         }
  //       }
  //     }
  //   }
  //   return result;
  // }

  @action.bound onClickAccordion = async (type: string) => {
    const designElementType = this.designElementTypes.find((e) => e.designElementType === type);

    if (!designElementType) {
      return;
    }

    const designElementsImageDic = {};
    await Promise.all(
      designElementType.designElements!.map(async (el) => {
        const originalId = el.designElementId!;

        if (libraryStore.designElementsImageDic[originalId]) return;

        let fetchId = originalId;

        const images = await this.fetchAllImages(fetchId, designElementType.designElementTypeId ?? 0);
        if (images) {
          designElementsImageDic[originalId] = [...images];
        }
      })
    );

    libraryStore.designElementsImageDic = {
      ...libraryStore.designElementsImageDic,
      ...designElementsImageDic,
    };
  };

  // Images: Plan View: (imageTypeId:0/1),  Location View(imageTypeId:2),  Other View(imageTypeId:3)
  @action.bound fetchAllImages = async (designElementId: number, designElementTypeId: number, isOtherViewOnly: boolean = false) => {
    libraryStore.incrementLoading();
    try {
      // const response = await SETAPI.StructureProfileController.getImagesByDesignElementIdUsingGET(designElementId);
      const response = await SETAPI.StructureProfileController.getMapImagesByElementIdAndDesignElementTypeIdUsingGET(designElementId, designElementTypeId);
      if (response && response.data) {
        const data: UserDefinedImages[] = response.data;
        // If user-generated plan view (imageTypeId:1) exists, it overrides system-generated plan view (imageTypeId:0)
        // If user-generated plan view does not exist, plan view is system-generated
        const result: UserDefinedImages[] = [];
        data.forEach((el: UserDefinedImages) => {
          if (isOtherViewOnly && (el.imageTypeId !== 3)) {
            return;
          };
          if (el.imageTypeId == 0 && data.some((elem: UserDefinedImages) => elem.imageTypeId == 1)) {
          } else {
            result.push(el);
          }
        });
        return result;
      }
    } catch (error) {
      MessageCenter.addError(
        `Failed to fetch images for designElementId ${designElementId}:` + (error as Error).message
      );
      // console.error(`Failed to fetch images for designElementId ${designElementId}:`, error);
      // throw error;
    } finally {
      libraryStore.decrementLoading();
    }
  };

  @action.bound fetchPlanLocationViewImages = async (sessionUuid: string) => {
    libraryStore.incrementLoading();
    try {
      let result: UserDefinedImages[] = [];

      const [planResponse, locationResponse] = await Promise.all([
        SETAPI.StructureProfileController.getImagebyDesignElementIdAndImageTypeIdUsingGET1(sessionUuid, 1),
        SETAPI.StructureProfileController.getImagebyDesignElementIdAndImageTypeIdUsingGET1(sessionUuid, 2)
      ]);

      if (planResponse && planResponse.data) {
        const planData = planResponse.data;
        const planResult = planData.map(el => {
          return {
            imageName: 'PlanView',
            imageTypeId: 1,
            images: el
          };
        });
        result.push(...planResult);
      }

      if (locationResponse && locationResponse.data) {
        const locationData = locationResponse.data;
        const locationResult = locationData.map(el => {
          return {
            imageName: 'LocationView',
            imageTypeId: 2,
            images: el
          };
        });
        result.push(...locationResult);
      }

      if (result) return result;

    } catch (error) {
      MessageCenter.addError(
        `Failed to fetch images for sessionUuid ${sessionUuid}:` + (error as Error).message
      );
    } finally {
      libraryStore.decrementLoading();
    }
  };

  @action.bound toggleImage(designElementId, image) {
    const imageIndex = this.selectedImages.findIndex(
      (selectedImage) =>
        selectedImage.stationId === image.stationId && selectedImage.designElementId === designElementId
    );
    if (imageIndex !== -1) {
      // Image is already selected, remove it from the array
      this.selectedImages = this.selectedImages.filter((_, index) => index !== imageIndex);
    } else {
      // Image is not selected, add it to the array
      this.selectedImages = [...this.selectedImages, { ...image, designElementId }];
    }
  }

  @action.bound removeImage(image: { stationId: string; imageURL: string }) {
    this.selectedImages = this.selectedImages.filter((selectedImage) => selectedImage.stationId !== image.stationId);
  }
}

export const getImage = async (projectId) => {
  MessageCenter.clearMessages();
  try {
    const response = await PRJAPI.MetadataImageController.getImageUsingGET(projectId);
    if (response.data) return response.data;
  } catch (error) {
    // MessageCenter.addError((error as Error).message);
    return;
  }
};

// fetching project data and session id
export const getProjectById = async (projectId) => {
  MessageCenter.clearMessages();
  try {
    const response = await PRJAPI.EftProjectController.getProjectByIdUsingGET(projectId);
    if (response.data) return response.data;
  } catch (error) {
    MessageCenter.addError((error as Error).message);
    return;
  }
};

// fetching design elements by session id
export const getAllDesignElementsBySession = async (sessionId) => {
  MessageCenter.clearMessages();
  try {
    const response = await SETAPI.DesignElementController.getAllDesignElementsBySessionUsingGET(sessionId);
    if (response.data) return response.data;
  } catch (error) {
    MessageCenter.addError((error as Error).message);
    return;
  }
};

// fetching surface data and surveys short data by session id
export const getSurfaceDefBySession = async (sessionId) => {
  MessageCenter.clearMessages();
  try {
    const response: any = await SETAPI.SurfaceDefController.getSurfaceDefBySessionUsingGET(sessionId);
    // console.log('getSurfaceDefBySessionUsingGET response:', response);
    if (response.data.length > 0) return response.data[0];
  } catch (error) {
    MessageCenter.addError((error as Error).message);
    return;
  }
};

// fetching surface id and surveys short data by session id
export const getSurfaceDefSurveyMetadataById = async (surfaceDefId) => {
  MessageCenter.clearMessages();
  try {
    const response: any = await SETAPI.SurfaceDefController.getSurfaceDefSurveyMetadataUsingGET(surfaceDefId);
    if (response.data.surveyMetadata) return response.data.surveyMetadata;
  } catch (error) {
    MessageCenter.addError((error as Error).message);
    return;
  }
};

// fetch all comments by projectId
/*     
Project types
Design Project: projectClassId === 1,
Analysis Project: projectClassId === 4

Comment types
Project Comments: commentTypeId  === 1   
Design Comments: commentTypeId  === 2  
*/
export const getComments = async (projectId: string, projectClassId?: number) => {
  libraryStore.incrementLoading();

  try {
    const response = await PRJAPI.ProjectCommentController.getProjectCommentsByProjectUuidUsingGET(projectId);
    if (response.data) {
      //Project level comments
      libraryStore.projectComments = [];
      libraryStore.projectComments = response.data.filter((data) => data.commentTypeId === 1);
      if (projectClassId === 4) {
        //Analysis Design comments
        libraryStore.analysisComments = [];
        libraryStore.analysisComments = response.data.filter((data) => data.commentTypeId === 2);
      } else if (projectClassId === 1) {
        // Structure Design comments
        libraryStore.structureComments = [];
        const structureCommentsResponse = await PRJAPI.ProjectElementCommentController.getProjectElementCommentsByProjectUuidUsingGET(
          projectId
        );
        if (structureCommentsResponse) libraryStore.structureComments = structureCommentsResponse.data;
      }
    }
  } catch (error) {
    MessageCenter.addError((error as Error).message);
  } finally {
    libraryStore.decrementLoading();
  }
};

export const projectTabs = [
  { tab: '1', title: 'Additional Project Details' },
  { tab: '2', title: 'Activity Summary' },
  { tab: '3', title: 'Comments' },
  { tab: '4', title: 'Project History' },
];
